import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ViewEncapsulation, TemplateRef, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import * as moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GroupService } from '../../../core/services/group.service';
import { HospitalService } from '../../../core/services/hospital.service';
import { UserService } from '../../../core/services/user.service';
import { StatisticsService, StatisticsData, ConnectFinancialStatistics } from '../../services/statistics.service';
import { isStateModeGroup } from '../../../shared/util/state-mode.util';
import { isConnectEnabledForGroup } from '../../../shared/util/connect.util';
import { LegendPosition, Color, ScaleType } from '@swimlane/ngx-charts';
import { HistoryChartDialogComponent } from 'src/app/shared/components/history-chart-dialog/history-chart-dialog.component';
import { StatisticsPdfExportService } from '../../services/statistics-pdf-export.service';
import { SocketService } from '../../../core/services/socket.service';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StatisticsComponent extends BaseComponent implements OnInit, AfterViewInit {

  group: any = null;
  statistics: StatisticsData | null = null;
  loading = false;
  error = false;
  statsType: 'clinical' | 'financial'  = 'clinical';

  /** A state unit has no financials (tasks/state-mode.md), so no Financial Stats either. */
  get showFinancialStats(): boolean {
    return !isStateModeGroup(this.group);
  }

  /** The EthiQal Recognition export: not for a state unit. */
  get showEthiqal(): boolean {
    return !isStateModeGroup(this.group);
  }

  /** After a practice change: a state unit is never left on Financial Stats. */
  keepStatsTypeAllowed(): void {
    if (!this.showFinancialStats) this.statsType = 'clinical';
  }
  // Date range filtering
  selectedDateRange: 'today' | 'week' | 'month' | 'year' | 'custom' = 'month';
  customStartDate: moment.Moment | null = null;
  customEndDate: moment.Moment | null = null;
  maxDate: moment.Moment = moment();

  // Hospital filtering
  hospitals: any[] = [];
  selectedHospitalId: string = '';

  // Date range chips
  dateRangeChips = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Computed properties to avoid inline function calls
  dateRangeDisplayText: string = 'This Month';
  formattedOverallWaitingTime: string = '';
  formattedTotalTimeSpent: string = '';
  formattedInvoiceAmount: string = '';
  formattedReceiptAmount: string = '';
  formattedOutstandingAmount: string = '';

  // Connect (medical-aid claims) financials
  connectEnabled: boolean = false;
  connectFinancials: ConnectFinancialStatistics | null = null;
  formattedConnectClaimedAmount: string = '';
  formattedConnectPaidAmount: string = '';
  formattedConnectOutstandingAmount: string = '';
  formattedConnectRejectedAmount: string = '';
  genderBreakdownArray: Array<{ label: string, count: number }> = [];
  ageDistribution: Array<{ name: string, value: number }> = [];
  genderPieData: Array<{ name: string, value: number, color?: string }> = [];
  referralPieData: Array<{ name: string, value: number, color?: string }> = [];
  admissionTypesPieData: Array<{ name: string, value: number }> = [];
  lengthOfStayChartData: Array<{ name: string, value: number }> = [];
  
  // Morbidity/Mortality data
  morbidityPieData: Array<{ name: string, value: number, color?: string }> = [];
  mortalityPieData: Array<{ name: string, value: number, color?: string }> = [];

  legendPosition: LegendPosition = LegendPosition.Right;

  // Custom color scheme for gender pie chart
  genderColorScheme: Color = {
    domain: ['#2196F3', '#E91E63', '#9E9E9E', '#757575'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Gender Colors'
  };

  // Custom color scheme for referral sources pie chart
  referralColorScheme: Color = {
    domain: ['#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#2196F3', '#FF5722', '#607D8B', '#795548'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Referral Colors'
  };

  // Custom color scheme for morbidity pie chart
  morbidityColorScheme: Color = {
    domain: ['#FF5722', '#FF9800', '#FFC107', '#FFEB3B', '#8BC34A', '#4CAF50', '#009688', '#00BCD4', '#2196F3', '#3F51B5'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Morbidity Colors'
  };

  // Custom color scheme for mortality pie chart
  mortalityColorScheme: Color = {
    domain: ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Mortality Colors'
  };

  pdfExportLoading = false;
  twelveMonthPdfExportLoading = false;

  // ── The Recognition Export's own date range ────────────────────────────────────────
  //
  // Deliberately SEPARATE from `selectedDateRange` / `customStartDate`. That filter drives the
  // on-screen figures and the "Export as PDF" report; this one drives a month-by-month breakdown.
  // Sharing them would mean the default "This Month" filter turned the Recognition Export into a
  // one-month report — silently, for anyone pressing the button they already knew.
  recognitionStartDate: moment.Moment | null = null;
  recognitionEndDate: moment.Moment | null = null;

  /**
   * Quick picks for the Recognition Export.
   *
   * "12 Months" reproduces the old button exactly — the menu's `applyPreset` shares the
   * whole-calendar-month rule with the server.
   */
  recognitionPresets = [
    { label: 'Last Month', months: 1 },
    { label: '6 Months', months: 6 },
    { label: '12 Months', months: 12 },
  ];

  /**
   * The range the CUSTOM filter is being edited to, before Apply.
   *
   * Held apart from `customStartDate`/`customEndDate` for the same reason the menu keeps its own
   * working copies: an abandoned edit must not change what the page is showing.
   */
  pendingCustomStartDate: moment.Moment | null = null;
  pendingCustomEndDate: moment.Moment | null = null;

  /**
   * Whether the range-picking Recognition Export is offered.
   *
   * HIDDEN, not deleted. The EthiQal Recognition button below replaces it for now, but it is the
   * general-purpose version — any range, consent included — and flipping this back is the whole
   * cost of restoring it. Deleting it would mean rebuilding the menu, the presets and the
   * server's range handling to get back to something that already works.
   */
  showRecognitionExport = false;

  ethiqalExportLoading = false;

  @ViewChild('exportOverlay') exportOverlayTemplate?: TemplateRef<unknown>;
  private exportOverlayRef: OverlayRef | null = null;
  
  // Full page loader properties
  /**
   * Whether an export is running — and, through the setter, whether the blocking overlay is up.
   *
   * A property rather than nine `openOverlay()` / `closeOverlay()` calls sprinkled through three
   * export flows, each with one success path and one error path. Those are exactly the call sites
   * where one gets forgotten, and a forgotten close leaves the practice staring at a modal
   * progress screen with no way out.
   */
  private _isGeneratingReport = false;

  get isGeneratingReport(): boolean {
    return this._isGeneratingReport;
  }

  set isGeneratingReport(value: boolean) {
    if (this._isGeneratingReport === value) return;
    this._isGeneratingReport = value;
    if (value) {
      this.openExportOverlay();
    } else {
      this.closeExportOverlay();
    }
  }
  currentLoadingMessage = '';
  currentSubMessage = '';
  exportProgress = 0;
  
  // Socket-based real-time updates
  private exportProgressSubscription: any = null;
  private exportSocketId: string = '';

  // Permission flag
  canViewStatistics: boolean = false;

  constructor(
    private statisticsService: StatisticsService,
    private groupService: GroupService,
    private hospitalService: HospitalService,
    private userService: UserService,
    private statisticsPdfExportService: StatisticsPdfExportService,
    private socketService: SocketService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.seedRecognitionRange();
    this.loadHospitals();
    this.checkStatisticsPermission();
    this.loadStatistics();
  }

  /**
   * Opens the Recognition menu on the last 12 whole months — what the button used to do without
   * asking.
   *
   * The rule is the server's: the current month counts only on its last day, so the breakdown
   * never ends on a month still in progress. Someone who wants exactly the old report opens the
   * menu and presses Apply.
   */
  private seedRecognitionRange(): void {
    const now = moment();
    const monthEnded = now.date() === now.clone().endOf('month').date();
    const lastMonth = monthEnded ? now.clone() : now.clone().subtract(1, 'month');

    this.recognitionEndDate = lastMonth.clone().endOf('month');
    this.recognitionStartDate = lastMonth.clone().subtract(11, 'months').startOf('month');
  }

  checkStatisticsPermission(): void {
    // Check if user is a doctor
    const isDoctor = this.userService.user?.userType?.toLowerCase() === 'doctor';

    // Subscribe to group users to get role and permissions
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((groupUsers: any[]) => {
        if (groupUsers && this.userService.user?.id) {
          const currentGroupUser = groupUsers.find((u: any) => u.userId === this.userService.user.id);
          
          if (currentGroupUser) {
            const isAdmin = currentGroupUser.userRole === 'Admin';
            const hasCanViewStatistics = currentGroupUser.canSeeFinancials || false;
            
            // User can view statistics if they are:
            // 1. A doctor
            // 2. An admin
            // 3. Have the "Can View Statistics" permission enabled
            this.canViewStatistics = isDoctor || isAdmin || hasCanViewStatistics;
          } else {
            // Default: allow viewing if user is a doctor
            this.canViewStatistics = isDoctor;
          }
        } else {
          // Default: allow viewing if user is a doctor
          this.canViewStatistics = isDoctor;
        }
      });
  }

  ngAfterViewInit(): void {
  }

  loadHospitals(): void {
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        this.hospitals = hospitals || [];
      });
  }

  loadStatistics(): void {
    this.loading = true;
    this.error = false;

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        if (group) {
          this.group = group;
          this.keepStatsTypeAllowed();
          const { startDate, endDate } = this.getDateRange();

          this.connectEnabled = isConnectEnabledForGroup(group);
          this.loadConnectFinancials(group.id, startDate, endDate);

          this.statisticsService.getStatistics(group.id, startDate, endDate, this.selectedHospitalId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                if (response.success) {
                  this.statistics = response.data;
                  this.updateComputedProperties();
                } else {
                  this.error = true;
                  this.showToastMessage('Failed to load statistics');
                }
                this.loading = false;
              },
              error: (error) => {
                console.error('Error loading statistics:', error);
                this.error = true;
                this.loading = false;
                this.showToastMessage('Error loading statistics');
              }
            });
        }
      });
  }

  private loadConnectFinancials(groupId: string, startDate: string, endDate: string): void {
    if (!this.connectEnabled) {
      this.connectFinancials = null;
      this.updateConnectComputedProperties();
      return;
    }

    this.statisticsService.getConnectFinancials(groupId, startDate, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.connectFinancials = data?.enabled ? data : null;
          this.updateConnectComputedProperties();
        },
        error: (error) => {
          console.error('Error loading connect financials:', error);
          this.connectFinancials = null;
          this.updateConnectComputedProperties();
        }
      });
  }

  private updateConnectComputedProperties(): void {
    const totals = this.connectFinancials?.totals;
    this.formattedConnectClaimedAmount = this.formatCurrency((totals?.billedCents || 0) / 100);
    this.formattedConnectPaidAmount = this.formatCurrency((totals?.paidCents || 0) / 100);
    this.formattedConnectOutstandingAmount = this.formatCurrency((totals?.outstandingCents || 0) / 100);
    this.formattedConnectRejectedAmount = this.formatCurrency((totals?.rejectedCents || 0) / 100);
  }

  onDateRangeChange(event: any): void {
    this.selectedDateRange = event.value;

    // Always seed the custom fields from the range that was showing, so the menu opens on
    // something real rather than on two empty boxes.
    const { startDate, endDate } = this.getDateRange();
    this.customStartDate = moment.utc(startDate).startOf('day');
    this.customEndDate = moment.utc(endDate).endOf('day');
    this.pendingCustomStartDate = this.customStartDate.clone();
    this.pendingCustomEndDate = this.customEndDate.clone();

    // ⚠ SELECTING "Custom" LOADS NOTHING. The chip opens a menu; the range is not chosen until
    // Apply. Reloading here would query the range the user is about to replace — and since the
    // seed above is whatever was previously showing, it would usually be the very query that
    // just ran. `onCustomRangeApplied` is the only path that loads a custom range.
    if (this.selectedDateRange === 'custom') {
      this.updateComputedProperties();
      return;
    }

    this.updateComputedProperties();
    this.loadStatistics();
  }

  /**
   * The custom range was applied from its menu. The only path that loads one.
   */
  onCustomRangeApplied(range: { startDate: moment.Moment; endDate: moment.Moment }): void {
    this.customStartDate = moment.utc(range.startDate).startOf('day');
    this.customEndDate = moment.utc(range.endDate).endOf('day');
    this.pendingCustomStartDate = this.customStartDate.clone();
    this.pendingCustomEndDate = this.customEndDate.clone();

    // Applying from the menu IS choosing a custom range — the chip is not necessarily selected
    // yet, because the menu can be opened while a preset chip is still active.
    this.selectedDateRange = 'custom';

    this.updateComputedProperties();
    this.loadStatistics();
  }

  onHospitalChange(event: any): void {
    this.selectedHospitalId = event.value;
    this.loadStatistics();
  }

  onCustomDateChange(): void {
    if (this.selectedDateRange === 'custom' && this.customStartDate && this.customEndDate) {
      this.updateComputedProperties();
      this.loadStatistics();
    }
  }

  private getDateRange(): { startDate: string; endDate: string } {
    let startDate: moment.Moment;
    let endDate: moment.Moment;

    switch (this.selectedDateRange) {
      case 'today':
        startDate = moment().utc().startOf('day');
        endDate = moment().utc().endOf('day');
        break;
      case 'week':
        startDate = moment().utc().startOf('week');
        endDate = moment().utc().endOf('week');
        break;
      case 'month':
        startDate = moment().utc().startOf('month');
        endDate = moment().utc().endOf('month');
        break;
      case 'year':
        startDate = moment().utc().startOf('year');
        endDate = moment().utc().endOf('year');
        break;
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          startDate = moment.utc(this.customStartDate).startOf('day');
          endDate = moment.utc(this.customEndDate).endOf('day');
        } else {
          startDate = moment().utc().startOf('month');
          endDate = moment().utc().endOf('month');
        }
        break;
      default:
        startDate = moment().utc().startOf('month');
        endDate = moment().utc().endOf('month');
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }

  getDateRangeDisplayText(): string {
    const { startDate, endDate } = this.getDateRange();
    const start = moment.utc(startDate);
    const end = moment.utc(endDate);

    if (this.selectedDateRange === 'custom') {
      return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
    }

    return this.dateRangeChips.find(chip => chip.value === this.selectedDateRange)?.label || 'This Month';
  }

  // Helper methods for template
  getGenderBreakdownText(): string {
    if (!this.statistics?.clinical.genderBreakdown) return 'N/A';

    const breakdown = this.statistics.clinical.genderBreakdown;
    return Object.entries(breakdown)
      .map(([gender, count]) => `${gender}: ${count}`)
      .join(', ');
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  }

  formatMinutes(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  getGenderBreakdownArray(): Array<{ label: string, count: number }> {
    if (!this.statistics?.clinical.genderBreakdown) return [];
    const breakdown = this.statistics.clinical.genderBreakdown;
    return Object.keys(breakdown)
      .sort((a, b) => a.localeCompare(b))
      .map(label => ({ label, count: breakdown[label] }));
  }

  private updateComputedProperties(): void {
    this.dateRangeDisplayText = this.getDateRangeDisplayText();
    this.formattedOverallWaitingTime = this.formatMinutes(this.statistics?.appointmentTypeOverview?.overallAvgWaitingTime || 0);
    this.formattedTotalTimeSpent = this.formatMinutes(this.statistics?.appointmentTypeOverview?.totalTimeSpentMinutes || 0);
    this.formattedInvoiceAmount = this.formatCurrency(this.statistics?.financial?.totalInvoiceAmount || 0);
    this.formattedReceiptAmount = this.formatCurrency(this.statistics?.financial?.totalReceiptAmount || 0);
    
    // Outstanding comes from the backend (sum of invoice outstandingBalance);
    // fall back to the old invoices - receipts subtraction against a stale server.
    const outstandingAmount = this.statistics?.financial?.totalOutstandingAmount !== undefined
      ? this.statistics.financial.totalOutstandingAmount
      : (this.statistics?.financial?.totalInvoiceAmount || 0) - (this.statistics?.financial?.totalReceiptAmount || 0);
    this.formattedOutstandingAmount = this.formatCurrency(outstandingAmount);
    
    this.genderBreakdownArray = this.getGenderBreakdownArray();
    this.ageDistribution = this.getAgeDistribution();
    this.genderPieData = this.getGenderPieData();
    this.referralPieData = this.getReferralPieData();
    this.admissionTypesPieData = this.getAdmissionTypesPieData();
    this.lengthOfStayChartData = this.getLengthOfStayChartData();
    this.morbidityPieData = this.getMorbidityPieData();
    this.mortalityPieData = this.getMortalityPieData();
  }

  getAgeDistribution(): Array<{ name: string, value: number }> {
    const ages: number[] = this.statistics?.clinical?.ages || [];
    const buckets = [
      { name: '0-10', min: 0, max: 10 },
      { name: '11-20', min: 11, max: 20 },
      { name: '21-30', min: 21, max: 30 },
      { name: '31-40', min: 31, max: 40 },
      { name: '41-50', min: 41, max: 50 },
      { name: '51-60', min: 51, max: 60 },
      { name: '61-70', min: 61, max: 70 },
      { name: '71-80', min: 71, max: 80 },
      { name: '81-90', min: 81, max: 90 },
      { name: '91+', min: 91, max: 200 }
    ];

    const counts = buckets.map(b => ({
      name: b.name,
      value: ages.filter(age => age >= b.min && age <= b.max).length
    }));

    return counts;
  }

  getGenderPieData(): Array<{ name: string, value: number, color?: string }> {
    if (!this.statistics?.clinical?.genderBreakdown) return [];
    
    const genderColors: { [key: string]: string } = {
      'male': '#2196F3',    // Blue
      'female': '#E91E63',  // Pink
      'other': '#9E9E9E',   // Light grey
      'unknown': '#757575'  // Dark grey
    };
    
    return Object.entries(this.statistics.clinical.genderBreakdown)
      .map(([name, value]) => ({ 
        name, 
        value, 
        color: genderColors[name.toLowerCase()] || '#757575' // Default to dark grey for unknown genders
      }))
      .sort((a, b) => {
        // Sort in order: male, female, other, unknown
        const order = { 'male': 0, 'female': 1, 'other': 2, 'unknown': 3 };
        const aOrder = order[a.name.toLowerCase() as keyof typeof order] ?? 4;
        const bOrder = order[b.name.toLowerCase() as keyof typeof order] ?? 4;
        return aOrder - bOrder;
      });
  }

  getReferralPieData(): Array<{ name: string, value: number, color?: string }> {
    if (!this.statistics?.clinical?.referralSources?.sources) {
      return [];
    }
    
    const referralColors: { [key: string]: string } = {
      'Google Search': '#4CAF50',           // Green
      'Social Media (Facebook, Instagram, etc.)': '#FF9800',  // Orange
      'Friend or Family Recommendation': '#9C27B0',           // Purple
      'Doctor Referral': '#F44336',         // Red
      'Website': '#2196F3',                 // Blue
      'Advertisement': '#FF5722',           // Deep Orange
      'Previous Patient': '#607D8B',        // Blue Grey
      'Other': '#795548'                    // Brown
    };
    
    // Create pie chart data from referral sources
    const sourcesData = this.statistics.clinical.referralSources.sources.map(source => ({
      name: source.source,
      value: source.count,
      color: referralColors[source.source] || '#9E9E9E' // Default to light grey for unknown sources
    }));

    // Add "No Referral Source" if there are patients without referral sources
    if (this.statistics.clinical.referralSources.withoutReferralSource > 0) {
      sourcesData.push({
        name: 'No Referral Source',
        value: this.statistics.clinical.referralSources.withoutReferralSource,
        color: '#E0E0E0' // Light grey for no referral source
      });
    }

    return sourcesData.sort((a, b) => b.value - a.value); // Sort by count descending
  }

  getAdmissionTypesPieData(): Array<{ name: string, value: number }> {
    if (!this.statistics?.inpatient?.admissionTypes) return [];
    return this.statistics.inpatient.admissionTypes.map(type => ({
      name: type.admissionType,
      value: type.count
    }));
  }

  getLengthOfStayChartData(): Array<{ name: string, value: number }> {
    if (!this.statistics?.inpatient?.lengthOfStayBreakdown) return [];
    
    // Ensure we have valid data and convert count to number
    return this.statistics.inpatient.lengthOfStayBreakdown
      .filter(item => item && item.lengthOfStayCategory && item.count !== undefined && item.count !== null)
      .map(item => ({
        name: item.lengthOfStayCategory,
        value: typeof item.count === 'string' ? parseInt(item.count) || 0 : (item.count || 0)
      }))
      .filter(item => item.value >= 0); // Ensure no negative values
  }

  getTotalScheduledEvents(): number {
    if (!this.statistics?.appointmentTypeOverview?.appointmentTypes) {
      return 0;
    }
    return this.statistics.appointmentTypeOverview.appointmentTypes.reduce((total, type) => total + type.totalScheduled, 0);
  }

  getCancellationRate(): number {
    if (!this.statistics?.appointmentTypeOverview?.totalAppointments || this.statistics.appointmentTypeOverview.totalAppointments === 0) {
      return 0;
    }
    return Math.round((this.statistics.appointmentTypeOverview.totalCancelled / this.statistics.appointmentTypeOverview.totalAppointments) * 100);
  }

  getNoShowRate(): number {
    if (!this.statistics?.appointmentTypeOverview?.totalAppointments || this.statistics.appointmentTypeOverview.totalAppointments === 0) {
      return 0;
    }
    return Math.round((this.statistics.appointmentTypeOverview.totalDidNotAttend / this.statistics.appointmentTypeOverview.totalAppointments) * 100);
  }

  openHistoryChart(metric: 'patientsSeen' | 'meanAge' | 'totalScheduledEvents' | 'avgWaitingTime' | 'totalTimeSpent' | 'totalCancelled' | 'totalDidNotAttend' | 'cancellationRate' | 'noShowRate' | 'ageDistribution' | 'genderBreakdown' | 'referralSources' | 'totalInpatients' | 'admissionTypes' | 'totalDischarges' | 'totalReadmissions' | 'avgLengthOfStay' | 'lengthOfStayBreakdown' | 'totalProcedures' | 'onboardingConsentsCompleted' | 'onboardingConsentsPending' | 'onboardingConsentsNotSent' | 'onboardingConsentsCompliance' | 'procedureConsentsCompleted' | 'procedureConsentsPending' | 'procedureConsentsNotSent' | 'procedureConsentsCompliance' | 'totalInvoices' | 'totalReceipts' | 'totalOutstanding' | 'morbidityCases' | 'mortalityCases'): void {
    const { startDate, endDate } = this.getDateRange();
    this.dialog.open(HistoryChartDialogComponent, {
      data: {
        groupId: this.group.id,
        metric,
        dateRange: this.selectedDateRange,
        currentRange: { startDate, endDate }
      },
      width: '70vw',
      maxWidth: '90vw'
    });
  }

  openPdfExportDialog(): void {
    if (!this.statistics) {
      this.showToastMessage('Please load statistics first before exporting');
      return;
    }

    this.pdfExportLoading = true;
    this.isGeneratingReport = true;
    this.exportProgress = 0;
    this.showToastMessage('Generating PDF...');

    // Start listening for progress updates immediately
    this.setupProgressListener();

    const { startDate, endDate } = this.getDateRange();

    this.statisticsPdfExportService.exportStatisticsToPdf(
      this.statistics,
      this.selectedDateRange,
      startDate,
      endDate,
      this.statsType,
      this.selectedHospitalId || undefined
    ).then((result) => {
      this.showToastMessage('PDF exported successfully');
      this.pdfExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    }).catch(error => {
      console.error('Error exporting PDF:', error);
      this.showToastMessage('Error generating PDF. Please try again.');
      this.pdfExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    });
  }

  /**
   * Runs the Recognition Export over the range applied in its menu.
   *
   * The range goes to the server as whole days; the server snaps it to whole calendar months,
   * because the report is a month-by-month table and a part month in it reads as a collapse in
   * activity rather than as a month that has not finished.
   */
  onRecognitionRangeApplied(range: { startDate: moment.Moment; endDate: moment.Moment }): void {
    this.recognitionStartDate = range.startDate.clone();
    this.recognitionEndDate = range.endDate.clone();
    this.runRecognitionExport();
  }

  /**
   * The last COMPLETED half-year: January–June, or July–December.
   *
   * Which half depends on where today falls, and the rule is simply "the most recent one that has
   * finished":
   *
   *   today in Jul–Dec  ->  Jan–Jun of THIS year      (the first half just closed)
   *   today in Jan–Jun  ->  Jul–Dec of LAST year      (this year's second half has not happened)
   *
   * The year is the half that matters. Reporting Jul–Dec of the CURRENT year while standing in
   * March would be a report on a period that has not occurred — six empty months presented as
   * fact. Concretely, in August 2026 this returns January–June 2026.
   *
   * No filters and no picker: the period is not a preference, it is whichever half the practice
   * is being recognised for, and letting someone choose it is how the wrong one gets submitted.
   */
  private lastCompletedHalfYear(): { startDate: moment.Moment; endDate: moment.Moment } {
    const now = moment();
    // `month()` is 0-based: 0-5 is Jan–Jun, 6-11 is Jul–Dec.
    const inFirstHalf = now.month() <= 5;

    if (inFirstHalf) {
      const year = now.year() - 1;
      return {
        startDate: moment({ year, month: 6, day: 1 }).startOf('month'),
        endDate: moment({ year, month: 11, day: 1 }).endOf('month'),
      };
    }

    const year = now.year();
    return {
      startDate: moment({ year, month: 0, day: 1 }).startOf('month'),
      endDate: moment({ year, month: 5, day: 1 }).endOf('month'),
    };
  }

  /** What the EthiQal button's tooltip says it will produce, so nobody has to guess. */
  get ethiqalPeriodLabel(): string {
    const { startDate, endDate } = this.lastCompletedHalfYear();
    return `${startDate.format('MMMM')} – ${endDate.format('MMMM YYYY')}`;
  }

  /**
   * The EthiQal Recognition report: one click, the last completed half-year, no consent data.
   */
  runEthiqalExport(): void {
    if (this.ethiqalExportLoading) return;

    const { startDate, endDate } = this.lastCompletedHalfYear();

    this.ethiqalExportLoading = true;
    this.isGeneratingReport = true;
    this.exportProgress = 0;
    this.showToastMessage(`Generating EthiQal Recognition report for ${this.ethiqalPeriodLabel}...`);

    this.setupProgressListener();

    this.statisticsPdfExportService.exportTwelveMonthStatisticsToPdf(
      this.statsType,
      this.selectedHospitalId || undefined,
      this.exportSocketId,
      startDate.toISOString(),
      endDate.toISOString(),
      {
        includeConsent: false,
        reportTitle: 'EthiQal Recognition Report',
        filePrefix: 'ethiqal-recognition',
      }
    ).then(() => {
      this.showToastMessage('EthiQal Recognition report exported successfully');
      this.ethiqalExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    }).catch(error => {
      console.error('Error exporting EthiQal Recognition report:', error);
      this.showToastMessage('Error generating EthiQal Recognition report. Please try again.');
      this.ethiqalExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    });
  }

  private runRecognitionExport(): void {
    if (!this.recognitionStartDate || !this.recognitionEndDate) return;
    if (this.twelveMonthPdfExportLoading) return;

    this.twelveMonthPdfExportLoading = true;
    this.isGeneratingReport = true;
    this.exportProgress = 0;
    this.showToastMessage('Generating Recognition report...');

    // Start listening for progress updates immediately
    // We'll get the export ID from the first progress message
    this.setupProgressListener();

    this.statisticsPdfExportService.exportTwelveMonthStatisticsToPdf(
      this.statsType,
      this.selectedHospitalId || undefined,
      this.exportSocketId,
      this.recognitionStartDate.clone().startOf('day').toISOString(),
      this.recognitionEndDate.clone().endOf('day').toISOString()
    ).then((result) => {
      this.showToastMessage('Recognition report exported successfully');
      this.twelveMonthPdfExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    }).catch(error => {
      console.error('Error exporting Recognition report:', error);
      this.showToastMessage('Error generating Recognition report. Please try again.');
      this.twelveMonthPdfExportLoading = false;
      this.isGeneratingReport = false;
      this.stopSocketProgressUpdates();
    });
  }


  // Socket-based real-time progress updates
  private setupProgressListener(): void {
    // Generate unique socket ID for this export session
    this.exportSocketId = this.generateRandomSocketId(25);
    
    // Join the export progress room with unique ID
    this.socketService.emit('joinExportProgress', { exportId: this.exportSocketId });
    
    // Listen for export progress updates using the unique socket ID
    this.exportProgressSubscription = this.socketService.listen(`export-progress-${this.exportSocketId}`, 'statistics-export')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.currentLoadingMessage = data.message;
        
        // Update submessage if provided
        if (data.subMessage) {
          this.currentSubMessage = data.subMessage;
        }
        
        // Update progress bar if progress value is provided
        if (data.progress !== null && data.progress !== undefined) {
          this.exportProgress = data.progress;
        }
        
        if (data.progress === 100 || data.completed) {
          this.exportProgress = 100;
          this.stopSocketProgressUpdates();
        }
      });
  }

  private generateRandomSocketId(length: number): string {
    const timestamp = new Date().getTime().toString();
    let result = '';
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length - timestamp.length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return (result + timestamp).toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 63);
  }

  private setupSocketProgressUpdates(exportId: string): void {
    // Join the export progress room
    this.socketService.emit('joinExportProgress', { exportId });
    
    // Listen for export progress updates
    this.exportProgressSubscription = this.socketService.listen(`export-progress-${exportId}`, 'statistics-export')
      .subscribe((data: any) => {
        this.currentLoadingMessage = data.message;
        if (data.progress === 100 || data.completed) {
          this.stopSocketProgressUpdates();
        }
      });
  }

  private stopSocketProgressUpdates(): void {
    if (this.exportProgressSubscription) {
      this.exportProgressSubscription.unsubscribe();
      this.exportProgressSubscription = null;
    }
    
    // Leave the export progress room
    if (this.exportSocketId) {
      this.socketService.emit('leaveExportProgress', { exportId: this.exportSocketId });
      this.exportSocketId = '';
    }
  }

  getMorbidityPieData(): Array<{ name: string, value: number, color?: string }> {
    const morbidityData = this.statistics?.clinical?.morbidityMortality?.morbidity?.breakdown || [];
    return morbidityData.map((item, index) => ({
      name: item.name,
      value: item.value,
      color: this.morbidityColorScheme.domain[index % this.morbidityColorScheme.domain.length]
    }));
  }

  getMortalityPieData(): Array<{ name: string, value: number, color?: string }> {
    const mortalityData = this.statistics?.clinical?.morbidityMortality?.mortality?.breakdown || [];
    return mortalityData.map((item, index) => ({
      name: item.name,
      value: item.value,
      color: this.mortalityColorScheme.domain[index % this.mortalityColorScheme.domain.length]
    }));
  }

  /**
   * Puts the progress overlay in the CDK overlay container, at the document root.
   *
   * ⚠ THIS IS THE FIX FOR THE OVERLAY RENDERING BEHIND THE LEFT NAVIGATION, and the reason it
   * could never have been fixed with a bigger z-index. Angular Material sets
   * `.mat-drawer-content { position: relative; z-index: 1 }` — which opens a STACKING CONTEXT
   * around every routed page. `.mat-drawer-side` is its sibling at `z-index: 3`, so the drawer is
   * painted above that entire context as a unit. `z-index: 9999999` on something inside it is
   * compared only against its siblings INSIDE the context; it can never climb out. The nav
   * therefore stayed lit and clickable on top of a supposedly blocking screen.
   *
   * `.cdk-overlay-container` is appended to `<body>` at `z-index: 1000`, in the root stacking
   * context, which is the only place a full-page overlay can actually cover the page.
   *
   * `blockScroll` rather than a backdrop: the overlay already covers everything and is not
   * dismissible — the export cannot be cancelled — so a backdrop would only add a surface that
   * looks clickable and is not.
   */
  private openExportOverlay(): void {
    if (this.exportOverlayRef || !this.exportOverlayTemplate) return;

    this.exportOverlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      hasBackdrop: false,
      panelClass: 'statistics-export-overlay-pane',
    });

    this.exportOverlayRef.attach(
      new TemplatePortal(this.exportOverlayTemplate, this.viewContainerRef)
    );
  }

  private closeExportOverlay(): void {
    this.exportOverlayRef?.dispose();
    this.exportOverlayRef = null;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stopSocketProgressUpdates();
    // ⚠ Not optional. The overlay lives OUTSIDE this component's DOM, so destroying the component
    // does not take it with it — navigating away mid-export would leave a progress screen welded
    // over the whole application with nothing left running to remove it.
    this.closeExportOverlay();
  }

} 